/**
 * `/admin/settings` - the six seeded keys (spec 12's own endpoint, NFR-10.6.1).
 *
 * `value` is a STRING on the wire because the column is TEXT; `value_type` says
 * how to read it and which control to render. The client never coerces - the
 * server validates against the type and returns the canonical form, because a
 * value that does not parse would break `setting_int` at read time inside
 * `v_member_access`, i.e. for every member rather than for this screen.
 *
 * There is no create and no delete: the six keys are named by ARCHITECTURE.md
 * and read by name from four services and one view.
 */

import { request } from "./client";
import type { SettingValueType, WithNotice } from "./types";

export type Setting = {
  key: string;
  value: string;
  value_type: SettingValueType;
  /** What the key drives, in plain English. Shown under the key on the settings
   *  screen, so it is written for an admin, not for us. */
  description: string | null;
  updated_by: string | null;
  updated_at: string;
};

export type SettingList = {
  items: Setting[];
  total: number;
} & WithNotice;

export type SettingUpdate = {
  setting: Setting;
  message: string;
};

export function fetchSettings(): Promise<SettingList> {
  return request<SettingList>("/admin/settings");
}

/**
 * `PATCH /admin/settings/{key}`.
 *
 * The response's `message` is deliberately worded to say the change is live
 * ("Members see this immediately"), which is FR-8.6's claim - the panel shows the
 * server's sentence rather than asserting it itself.
 */
export function updateSetting(key: string, value: string): Promise<SettingUpdate> {
  return request<SettingUpdate>(`/admin/settings/${encodeURIComponent(key)}`, {
    method: "PATCH",
    body: { value },
  });
}
